import { AfterViewInit, Component, Input, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { ChangeDetectorRef } from '@angular/core';

import { languageService } from '@unpispas/upp-base';
import { PlacePanel } from '@unpispas/upp-base';
import { viewService } from '@unpispas/upp-base';
import { platformService } from '@unpispas/upp-base';
import { alertService } from '@unpispas/upp-base';
import { deviceService } from '@unpispas/upp-base';
import { dataService } from '@unpispas/upp-data';
import { FloorArea } from '@unpispas/upp-data';
import { loginService } from '@unpispas/upp-data';
import { Pinpad } from '@unpispas/upp-data';
import { PlaceView } from '@unpispas/upp-data';
import { RasppiView } from '@unpispas/upp-data';
import { UserView } from '@unpispas/upp-data';

import { UppPinpadWorkComponent } from '../work/upp-work';

/**
 * Pinpad home shell: place header, linkage warnings, work screen, and settings.
 * Follows POS mobile panel pattern (`place/upp-view`, `place/upp-cart`): one shell
 * `upp-panel`; main chrome/tabs hide while ticket/table Attention/cart fill `panel-content`.
 * Work stays mounted under `[hidden]` (RN-PTF03-09). Settings is a sibling panel.
 * Ticket/mesa/cart hosts are provided by the app via TemplateRef inputs (RN-PPS-03).
 */
@Component({
    selector: 'upp-pinpad-home',
    templateUrl: './upp-home.html',
    styleUrl: './upp-home.scss',
})
export class UppPinpadHomeComponent implements AfterViewInit, OnDestroy {
    /***********************************************/
    /* INPUTS — app-composed panel hosts           */
    /***********************************************/

    /** App template for `upp-ticket-edit` when work shows ticket detail. */
    @Input() ticketDetailTpl: TemplateRef<{ $implicit: UppPinpadWorkComponent }> | null = null;

    /** App template for `upp-table-edit` when work shows table Attention. */
    @Input() tableDetailTpl: TemplateRef<{ $implicit: UppPinpadWorkComponent }> | null = null;

    /** App template for cart host (`app-ptf-cart`). */
    @Input() cartTpl: TemplateRef<unknown> | null = null;

    /** App template for tickets list (`app-ptf-tickets`), forwarded to work. */
    @Input() ticketsTpl: TemplateRef<{ $implicit: UppPinpadWorkComponent }> | null = null;

    /***********************************************/
    /* VIEW CHILD                                  */
    /***********************************************/

    /** Template ref so tests can declare a stub with the same selector. */
    @ViewChild('workHost') work: UppPinpadWorkComponent | null = null;

    /***********************************************/
    /* MEMBER VARIABLES                            */
    /***********************************************/

    private _showSettings = false;
    private _serviceReachable = false;
    private _serviceProbing = false;
    private _probeToken = 0;
    private _probeTimer: ReturnType<typeof setTimeout> | null = null;

    /***********************************************/
    /* CONSTRUCTOR                                 */
    /***********************************************/

    constructor(
        private lang: languageService,
        private data: dataService,
        private login: loginService,
        private alertCtrl: alertService,
        private change: ChangeDetectorRef,
        private platform: platformService,
        private devices: deviceService,
        public view: viewService,
    ) {}

    /***********************************************/
    /* GETTERS                                     */
    /***********************************************/

    /**
     * True when this session `PosDevice` has an active payment terminal
     * on the current place (`PosDevice.pinpad`).
     */
    get Linked(): boolean {
        return !!this._SessionPinpad();
    }

    /** Show «not a terminal» warning when hardware does not seem phone-sized / mobile. */
    get ShowNotTerminalWarning(): boolean {
        return !this.platform.SeemsMobile;
    }

    /** Show «unlinked» warning when no payment terminal is linked to this device. */
    get ShowUnlinkedWarning(): boolean {
        return !this.Linked;
    }

    get place(): PlaceView | null {
        return this.data.alives.get(this.data.place) as PlaceView;
    }

    /** Hide «change place» when the user only has one place (auto-enter would bounce back). */
    get CanChangePlace(): boolean {
        const user = this.data.alives.get(this.data.user) as UserView | null;
        return (user?.places?.length ?? 0) > 1;
    }

    get ShowSettings(): boolean {
        return this._showSettings;
    }

    /** Zoom percent for the settings range (50–100). */
    get ZoomPercent(): number {
        return this.view.NormalizeZoomPercent(Math.round((this.view.ZoomLevel || 1) * 100), 50, 100);
    }

    /** Active UI language code for the settings language picker. */
    get CurrentLanguage(): string {
        return this.lang.GetLanguage(true);
    }

    /**
     * Print-service id configured for this device from the POS (read-only here).
     */
    get ConfiguredServiceName(): string {
        return String(this._ActiveService()?.id || this.place?.connect?.rasppi?.id || '').trim();
    }

    get ServiceStatusKind(): 'CHECK' | 'ONLINE' | 'OFFLINE' {
        if (this._serviceProbing || !this._IsServiceComplete) {
            return 'CHECK';
        }

        return this._serviceReachable ? 'ONLINE' : 'OFFLINE';
    }

    get ServiceStatusTitle(): string {
        switch (this.ServiceStatusKind) {
            case 'ONLINE':
                return this.tr('@place_printsvc_online_title');
            case 'OFFLINE':
                return this.tr('@place_printsvc_offline_warning');
            default:
                return this.tr('@place_device_connection_incomplete');
        }
    }

    get ServiceStatusMessage(): string {
        switch (this.ServiceStatusKind) {
            case 'ONLINE':
                return this.tr('@place_printsvc_online_message');
            case 'OFFLINE':
                return this.tr('@place_printsvc_offline_message');
            default:
                return this.tr('@place_printsvc_check_message');
        }
    }

    get ServiceStatusIcon(): string {
        switch (this.ServiceStatusKind) {
            case 'ONLINE':
                return 'cloud-done-outline';
            case 'OFFLINE':
                return 'cloud-offline-outline';
            default:
                return 'cloud-upload-outline';
        }
    }

    get ServiceStatusColor(): string {
        switch (this.ServiceStatusKind) {
            case 'ONLINE':
                return 'success';
            case 'OFFLINE':
                return 'danger';
            default:
                return 'warning';
        }
    }

    private get _IsServiceComplete(): boolean {
        return !!this.ConfiguredServiceName || !!this._ServiceUri;
    }

    /** Direct-mode URI from the place device connection (configured on POS). */
    private get _ServiceUri(): string | null {
        const _uri = this.place?.connect?.connect;
        return _uri ? String(_uri).trim() || null : null;
    }

    /***********************************************/
    /* LIFECYCLE                                   */
    /***********************************************/

    tr(key: string): string {
        return this.lang.tr(key);
    }

    /** Sync header bindings that depend on the work ViewChild once it is available. */
    ngAfterViewInit(): void {
        this.change.detectChanges();
    }

    ngOnDestroy(): void {
        this._StopServiceProbe();
    }

    /***********************************************/
    /* SETTINGS SHELL                              */
    /***********************************************/

    OnOpenSettings(): void {
        this._showSettings = true;
        this._EvaluateServiceStatus();
        this._ScheduleServiceProbe();
        this.change.markForCheck();
    }

    OnCloseSettings(): void {
        this._showSettings = false;
        this._StopServiceProbe();
        this.change.markForCheck();
    }

    FloorTrack(_index: number, area: FloorArea): string | null {
        return area?.objid || area?._uuid || null;
    }

    /** Header map icon: show/hide the horizontal floor picker. */
    OnFloorOptions(): void {
        this.work?.OnFloorOptions();
        this.change.markForCheck();
    }

    /** Selects grid (null) or a floor area via the work shell. */
    OnSelectFloor(area: FloorArea | null): void {
        this.work?.OnSelectFloor(area);
        this.change.markForCheck();
    }

    /** Footer tab bar → work shell (Mesas / Tickets / Caja). */
    OnWorkTab(tabname: string): void {
        this.work?.OnTab(tabname);
        this.change.markForCheck();
    }

    /** Ticket / cart PlacePanel → work router (surfaces hosted as home siblings). */
    async OnWorkPanel(panel: PlacePanel): Promise<boolean> {
        const allowed = (await this.work?.OnPanel(panel)) ?? false;
        this.change.markForCheck();
        return allowed;
    }

    /***********************************************/
    /* VISUALIZATION OPTIONS                       */
    /***********************************************/

    /**
     * Applies the selected UI language immediately (same path as user profile picker).
     * @param language Language code from `upp-language-select`.
     */
    OnLanguage(language: string): void {
        const next = String(language || '').toUpperCase();
        if (!next || next === this.lang.GetLanguage(true)) {
            return;
        }

        void this.lang.SetLanguage(next);
        this.change.markForCheck();
    }

    /**
     * Persists scrollbar visibility on the device view cache.
     * @param event Ionic checkbox change with `detail.checked`.
     */
    OnScrollbar(event: { detail?: { checked?: boolean } }): void {
        this.view.Scrollbar = !!event?.detail?.checked;
        this.change.markForCheck();
    }

    /** Toggles light/dark theme and writes the device view cache. */
    OnChangeTheme(): void {
        this.view.Theme = this.view.LightTheme ? 'dark' : 'light';
        this.change.markForCheck();
    }

    OnZoomPercent(event: { detail?: { value?: number | { lower: number; upper: number } } }): void {
        if (!this.view.CanZoom) {
            return;
        }

        const value = event?.detail?.value;
        const raw = Number(
            typeof value === 'number' ? value : (value?.lower ?? this.ZoomPercent),
        );
        const percent = this.view.NormalizeZoomPercent(raw, 50, 100);
        this.view.ZoomLevel = percent / 100;
        this.change.markForCheck();
    }

    /***********************************************/
    /* PRINT SERVICE (read-only; configured on POS)*/
    /***********************************************/

    /** Re-probes reachability of the place-configured print service. */
    async OnRefreshServiceStatus(): Promise<void> {
        this._StopServiceProbe(false);
        await this._ProbeServiceStatus();
    }

    /***********************************************/
    /* PLACE / SESSION                             */
    /***********************************************/

    /** Leave the place and return to the place picker (USER view). */
    OnChangePlace(): void {
        this.data.SetPlace(null).then(() => {
            this.view.View = 'USER';
        });
    }

    /** Confirm and close the staff session. */
    async OnLogout(): Promise<void> {
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@menu_logout_confirm_title'),
            message: this.lang.tr('@menu_logout_confirm_message'),
            backdropDismiss: false,
            buttons: [
                {
                    text: this.lang.tr('@yes'),
                    handler: () => {
                        setTimeout(() => {
                            this._BlurActive();
                            alert.dismiss(true);
                        }, 100);
                        return false;
                    },
                },
                {
                    text: this.lang.tr('@no'),
                    handler: () => {
                        setTimeout(() => {
                            this._BlurActive();
                            alert.dismiss(false);
                        }, 100);
                        return false;
                    },
                },
            ],
        });

        alert.onDidDismiss().then((detail: { data?: boolean } | undefined) => {
            if (detail && detail.data) {
                this.login.DoLogout();
            }
        });

        await alert.present();
    }

    /***********************************************/
    /* PRIVATE — SERVICE STATUS                    */
    /***********************************************/

    private _EvaluateServiceStatus(): void {
        if (!this._IsServiceComplete) {
            this._serviceReachable = false;
            return;
        }

        if (this._ServiceUri) {
            return;
        }

        const _connect = this.place?.connect;
        const _serviceId = this.ConfiguredServiceName;
        this._serviceReachable =
            !!_connect?.hassvc && !!_serviceId && _connect.rasppi?.id === _serviceId;
    }

    private _ScheduleServiceProbe(): void {
        this._StopServiceProbe(false);

        if (this._ServiceUri) {
            this._serviceProbing = true;
            this._serviceReachable = false;
            this.change.markForCheck();
        }

        this._probeTimer = setTimeout(() => {
            this._probeTimer = null;
            void this._ProbeServiceStatus();
        }, 300);
    }

    private _StopServiceProbe(bumpToken = true): void {
        if (this._probeTimer != null) {
            clearTimeout(this._probeTimer);
            this._probeTimer = null;
        }

        if (bumpToken) {
            this._probeToken++;
        }
    }

    private async _ProbeServiceStatus(): Promise<void> {
        const _token = ++this._probeToken;
        const _serviceId = this.ConfiguredServiceName;
        const _uri = this._ServiceUri;

        if (!_serviceId && !_uri) {
            this._serviceProbing = false;
            this._serviceReachable = false;
            this.change.markForCheck();
            return;
        }

        if (_uri) {
            const _parts = _uri.split(':');
            if (_parts.length !== 2) {
                this._serviceProbing = false;
                this._serviceReachable = false;
                this.change.markForCheck();
                return;
            }

            const _host = _parts[0]?.trim() || null;
            const _port = Number(_parts[1]?.trim());
            const _hint = _serviceId || 'local';

            this._serviceProbing = true;
            this.change.markForCheck();

            const _connection = await this.devices.Test(_host, _port, _hint, true);
            if (_token != this._probeToken) {
                return;
            }

            this._serviceProbing = false;
            this._serviceReachable = !!_connection?.svcname;
            this.change.markForCheck();
            return;
        }

        this._serviceProbing = true;
        this.change.markForCheck();

        const _connection = await this.devices.Test(null, null, _serviceId, false);
        if (_token != this._probeToken) {
            return;
        }

        this._serviceProbing = false;
        this._serviceReachable = !!_connection;
        this.change.markForCheck();
    }

    /**
     * Print service for this device: session connect first, then the RASPPI that
     * owns the linked Pinpad (POS linkage), then the sole place service.
     */
    private _ActiveService(): RasppiView | null {
        const _connected = this.place?.service;
        if (_connected) {
            return _connected;
        }

        const _linked = this._LinkedTerminalService();
        if (_linked) {
            return _linked;
        }

        if (!this.place || this.place.MultiplePOS) {
            return null;
        }

        return this.place.services[0] ?? null;
    }

    /** RASPPI that owns the Pinpad linked to this session device, if any. */
    private _LinkedTerminalService(): RasppiView | null {
        const _rasppi = this._SessionPinpad()?.rasppi ?? null;
        if (!_rasppi?.IsValid) {
            return null;
        }

        return this.data.alives.get(_rasppi) as RasppiView;
    }

    /** Active terminal for the session `PosDevice` (`PosDevice.pinpad`). */
    private _SessionPinpad(): Pinpad | null {
        return this.data.session?.device?.pinpad ?? null;
    }

    private _BlurActive(): void {
        const active = document.activeElement as HTMLElement | null;
        if (active) {
            active.blur();
        }
    }
}
